import React, { useState, useEffect } from 'react';
import { ViewState, Runbook, OrderReceipt } from './types';
import { loadStoredState, saveStoredState } from './lib/storage';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { CheckoutModal } from './components/CheckoutModal';
import { HomeView } from './views/HomeView';
import { CatalogView } from './views/CatalogView';
import { DetailView } from './views/DetailView';
import { ReaderView } from './views/ReaderView';
import { CompilerView } from './views/CompilerView';
import { TerminalView } from './views/TerminalView';
import { ToolsHubView } from './views/ToolsHubView';

export const App: React.FC = () => {
  const [state, setState] = useState(() => loadStoredState());
  const [currentView, setCurrentView] = useState<ViewState>('home');
  const [activeSlug, setActiveSlug] = useState<string>('sprint-pricing-architecture');
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [lastBuyerEmail, setLastBuyerEmail] = useState<string>('');

  useEffect(() => {
    saveStoredState(state);
  }, [state]);

  const activeRunbook = state.catalog.find((rb) => rb.slug === activeSlug) || state.catalog[0];

  const handleOpenDetail = (slug: string) => {
    setActiveSlug(slug);
    setCurrentView('detail');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOpenReader = (slug: string, email?: string) => {
    setActiveSlug(slug);
    if (email) setLastBuyerEmail(email);
    setCurrentView('reader');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCompletePurchase = (email: string) => {
    if (!activeRunbook) return;

    const isAlreadyUnlocked = state.unlockedSlugs.includes(activeRunbook.slug);
    const newUnlocked = isAlreadyUnlocked
      ? state.unlockedSlugs
      : [...state.unlockedSlugs, activeRunbook.slug];

    const newOrder: OrderReceipt = {
      id: `tx_${Math.random().toString(36).slice(2, 8)}`,
      title: activeRunbook.title,
      slug: activeRunbook.slug,
      price: activeRunbook.price,
      email,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      method: 'Stripe Card (Direct)',
    };

    const newLeads = state.leads.includes(email) ? state.leads : [...state.leads, email];

    setState((prev) => ({
      ...prev,
      unlockedSlugs: newUnlocked,
      orders: [newOrder, ...prev.orders],
      leads: newLeads,
    }));

    setLastBuyerEmail(email);
    setCurrentView('reader');
  };

  const handlePublishCompiled = (runbook: Runbook) => {
    setState((prev) => ({
      ...prev,
      catalog: [runbook, ...prev.catalog],
      unlockedSlugs: [...prev.unlockedSlugs, runbook.slug],
    }));
    setActiveSlug(runbook.slug);
    setCurrentView('catalog');
  };

  const handleUnlockTool = (targetId: string, email: string, price: number, planName: string) => {
    const isAlreadyUnlocked = state.unlockedTools.includes(targetId);
    const newUnlockedTools = isAlreadyUnlocked
      ? state.unlockedTools
      : [...state.unlockedTools, targetId];

    const newOrder: OrderReceipt = {
      id: `tx_${Math.random().toString(36).slice(2, 8)}`,
      title: planName,
      slug: targetId,
      price,
      email,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      method: 'Stripe Card (Micro-Tool)',
    };

    const newLeads = state.leads.includes(email) ? state.leads : [...state.leads, email];

    setState((prev) => ({
      ...prev,
      unlockedTools: newUnlockedTools,
      orders: [newOrder, ...prev.orders],
      leads: newLeads,
    }));
  };

  const handleManualSale = (receipt: OrderReceipt) => {
    setState((prev) => ({
      ...prev,
      orders: [receipt, ...prev.orders],
      leads: prev.leads.includes(receipt.email) ? prev.leads : [...prev.leads, receipt.email],
    }));
  };

  const handleUpdateStripeLink = (link: string) => {
    setState((prev) => ({ ...prev, stripePaymentLink: link }));
  };

  return (
    <div className="min-h-screen flex flex-col antialiased">
      <Header
        currentView={currentView}
        onNavigate={(view) => {
          setCurrentView(view);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        unlockedCount={state.unlockedSlugs.length}
        onOpenUnlocked={() => {
          if (state.unlockedSlugs.length > 0) {
            handleOpenReader(state.unlockedSlugs[0]);
          }
        }}
      />

      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {currentView === 'home' && (
          <HomeView
            onNavigate={setCurrentView}
            onOpenDetail={handleOpenDetail}
            featuredRunbook={state.catalog[0]}
          />
        )}

        {currentView === 'tools' && (
          <ToolsHubView
            unlockedTools={state.unlockedTools}
            onUnlockTool={handleUnlockTool}
            stripePaymentLink={state.stripePaymentLink}
          />
        )}

        {currentView === 'catalog' && (
          <CatalogView
            catalog={state.catalog}
            unlockedSlugs={state.unlockedSlugs}
            onOpenDetail={handleOpenDetail}
          />
        )}

        {currentView === 'detail' && (
          <DetailView
            runbook={activeRunbook}
            isUnlocked={state.unlockedSlugs.includes(activeRunbook.slug)}
            onOpenCheckout={() => setIsCheckoutOpen(true)}
            onOpenReader={handleOpenReader}
            onBackToCatalog={() => setCurrentView('catalog')}
          />
        )}

        {currentView === 'reader' && (
          <ReaderView
            runbook={activeRunbook}
            ownerEmail={lastBuyerEmail}
            onBack={() => setCurrentView('catalog')}
          />
        )}

        {currentView === 'compiler' && (
          <CompilerView
            onPublishToCatalog={handlePublishCompiled}
            onOpenReader={(slug) => handleOpenReader(slug, 'Author Pass')}
          />
        )}

        {currentView === 'terminal' && (
          <TerminalView
            orders={state.orders}
            leads={state.leads}
            stripePaymentLink={state.stripePaymentLink}
            onUpdateStripeLink={handleUpdateStripeLink}
            onLogManualSale={handleManualSale}
          />
        )}
      </main>

      <Footer />

      <CheckoutModal
        runbook={activeRunbook}
        isOpen={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        onCompletePurchase={handleCompletePurchase}
        stripePaymentLink={state.stripePaymentLink}
      />
    </div>
  );
};

export default App;
